import { useState } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { orderBy } from "lodash";
import { DataQuery, Block } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { BlockOption, UseGetBlocksRangesReturn } from "@services/types/blocks/blocksTypes";
import blocksRangesQuery from "./graphql/blocksRangesData.query.graphql";

export const useGetBlocksRanges = (): UseGetBlocksRangesReturn => {
  const { scenario }: IParams = useParams();
  const client = useApolloClient();
  const [dataStart, setDataStart] = useState<BlockOption[]>(null);
  const [dataEnd, setDataEnd] = useState<BlockOption[]>(null);
  const [rawBlocks, setRawBlocks] = useState<Block[]>(null);
  const [isLoading, setIsLoading] = useState(false);

  const variables = {
    scenarioId: scenario,
  };

  const uniqueValues = (value: string[][]) => {
    const options = [];
    const unique = value.flat().filter((item, pos) => value.flat().indexOf(item) === pos);
    unique.map((hour: any) => options.push({ label: hour.replace(":00", ""), value: hour }));
    return options;
  };

  const formatOptionsRanges = (blocks: Block[]) => {
    let startOptions = [];
    let endOptions = [];
    if (blocks) {
      // array of starting and ending time
      const timesStart = blocks.map(item => [item.startingTime]);
      const timesEnd = blocks.map(item => [item.endingTime]);
      // filter unique value
      startOptions = uniqueValues(timesStart);
      endOptions = uniqueValues(timesEnd);
    }

    return {
      startOptions: orderBy(startOptions, ["asc"]),
      endOptions: orderBy(endOptions, ["asc"]),
    };
  };

  const getBlocksInfo = async (shouldResetData = false) => {
    try {
      if (shouldResetData) {
        setDataStart(null);
        setDataEnd(null);
        setRawBlocks(null);
      }

      setIsLoading(true);
      const { data, errors } = await client.query({
        query: blocksRangesQuery,
        variables,
        fetchPolicy: "no-cache",
      });

      if (errors) {
        console.debug("[GetBlocks error]", errors);
        setIsLoading(false);
        return null;
      }

      const dataQuery: DataQuery = data.data;
      const format = formatOptionsRanges(dataQuery.allBlocks);

      if (format.startOptions && format.endOptions) {
        setDataStart(format.startOptions);
        setDataEnd(format.endOptions);
        setRawBlocks(dataQuery.allBlocks);
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  return [
    {
      dataStart,
      dataEnd,
      rawBlocks,
      isLoading,
    },
    getBlocksInfo,
  ];
};

export default useGetBlocksRanges;
